import { useCallback, useEffect, useRef, useState } from 'react'
import Popper from '@mui/material/Popper/Popper'
import { styled } from '@mui/material'
import { colorPalette } from '../../../../style/colorPalette'
import {
  TextFilter as TextFilterModel,
  TextFilterOperator,
  TextFilterOperatorLabel,
} from '../../../../containers/BulkSheetView/components/filter/TextFilter'
import {
  BulkSheetFilter,
  FilterSelect,
  FilterInput,
  FilterFooter,
} from '../../../../containers/BulkSheetView/components/filter/common'

const BLANK_OPERATORS = [TextFilterOperator.BLANK, TextFilterOperator.NOT_BLANK]

const buildTextFilterModel = (
  operator: TextFilterOperator,
  value: string
): TextFilterModel | null => {
  const isBlankOp = BLANK_OPERATORS.includes(operator)
  const isActive = isBlankOp || !!value
  return isActive ? { operator, value: isBlankOp ? '' : value } : null
}

type Props = {
  anchorEl: Element | undefined
  valueGetter: (params: any) => string
  filterChangedCallback: (changed: TextFilterModel | undefined) => void
  defaultValue?: TextFilterModel | undefined
}

export const TextFilter = ({
  anchorEl,
  filterChangedCallback,
  defaultValue,
}: Props) => {
  const [model, setModel] = useState<TextFilterModel | null>(
    defaultValue ?? null
  )
  const inputRef = useRef<HTMLInputElement>(null)
  const initializedRef = useRef(false)

  // ポッパーが閉じたらリセット
  useEffect(() => {
    if (!anchorEl) {
      initializedRef.current = false
    }
  }, [anchorEl])

  // ポッパーが開いたら defaultValue で初期化
  useEffect(() => {
    if (anchorEl && !initializedRef.current) {
      initializedRef.current = true
      setModel(defaultValue ?? null)
    }
  }, [anchorEl, defaultValue])

  const handleModelChange = useCallback(
    (newModel: TextFilterModel | null) => {
      setModel(newModel)
      filterChangedCallback(newModel ?? undefined)
    },
    [filterChangedCallback]
  )

  const operator =
    (model?.operator as TextFilterOperator) ?? TextFilterOperator.CONTAINS
  const value = model?.value ?? ''

  return (
    <StyledPopper open={true} anchorEl={anchorEl} placement={'bottom-start'}>
      <StyledFilterContainer>
        <BulkSheetFilter>
          <FilterSelect
            variant="outlined"
            value={operator}
            onChange={e =>
              handleModelChange(
                buildTextFilterModel(
                  e.target.value as TextFilterOperator,
                  value
                )
              )
            }
            sx={{ width: '100%' }}
          >
            {Object.values(TextFilterOperator).map(v => (
              <option key={`text-filter-option-${v}`} value={v}>
                {TextFilterOperatorLabel[v]}
              </option>
            ))}
          </FilterSelect>
          <FilterInput
            inputRef={inputRef}
            value={value}
            onChange={e =>
              handleModelChange(buildTextFilterModel(operator, e.target.value))
            }
            placeholder={'Filter...'}
            sx={{ height: 22 }}
          />
          <FilterFooter
            loading={false}
            onClick={() => handleModelChange(null)}
          />
        </BulkSheetFilter>
      </StyledFilterContainer>
    </StyledPopper>
  )
}

const StyledPopper = styled(Popper)({
  boxShadow: '0px 4px 16px 0px #7B8CAA80',
  background: colorPalette.monotone[0],
  display: 'flex',
  margin: '10px',
  borderRadius: '4px',
  zIndex: 1300,
})

const StyledFilterContainer = styled('div')({
  display: 'flex',
  margin: 'auto 10px',
})
